import { useContext } from 'react';

import type {
  ChatConversation,
  ChatIntentMessage,
  DefaultChatMessage,
} from '../../../../common';
import { isMessageSupportedIntent } from '../../../../utils/message';
import BaseMessage from './Base';
import Intent from './Intent';
import AppointmentConfirmed from './GeneralUi/AppointmentConfirmed';
import Recommendation, {
  SUPPORTED_INTENT_RECOMMENDATION,
} from './Recommendation';
import { ConfigurationContext } from '../../../../context/configurationContext';
import { isEnumMember } from 'common/dist/utils/types';

import * as api from '../../../../api';

interface Props {
  message: ChatConversation;
  showTextAnimation: boolean;
}

interface ResponseEntitiesWithRecommendation
  extends api.Assistant.ResponseEntities {
  recommendation: api.Assistant.Recommendation;
}

const isResponseMessage = (
  message: ChatConversation,
): message is DefaultChatMessage | ChatIntentMessage => {
  return 'response_entities' in message;
};

const isRecommendationSupported = (
  responseEntities: api.Assistant.ResponseEntities,
  intent: string,
): responseEntities is ResponseEntitiesWithRecommendation => {
  if (!responseEntities.recommendation) return false;
  if (!isEnumMember(api.Assistant.Intent, intent)) return false;
  const supportedIntents = SUPPORTED_INTENT_RECOMMENDATION[intent];
  if (!supportedIntents?.includes(responseEntities.recommendation)) {
    return false;
  }
  return true;
};

const Message: React.FC<Props> = ({ message, showTextAnimation }) => {
  const configurationContext = useContext(ConfigurationContext);

  if (
    configurationContext?.authenticationEnabled &&
    isResponseMessage(message) &&
    isRecommendationSupported(message.response_entities, message.intent)
  ) {
    return (
      <Recommendation
        recommendation={message.response_entities.recommendation}
        message={message}
        showTextAnimation={showTextAnimation}
      />
    );
  }

  if (
    'intent' in message &&
    message.intent === api.Assistant.Intent.GeneralInformation &&
    'intentComponent' in message &&
    message.intentComponent
  ) {
    switch (message.intentComponent) {
      case api.Assistant.IntentComponent.GeneralInformationAppointmentConfirmed:
        return (
          <AppointmentConfirmed
            message={message}
            showTextAnimation={showTextAnimation}
          />
        );
    }
  }

  if (isMessageSupportedIntent(message)) {
    return <Intent message={message} showTextAnimation={showTextAnimation} />;
  }

  return (
    <BaseMessage message={message} showTextAnimation={showTextAnimation} />
  );
};
export default Message;
